import Status from '@/app/constants/status';
import { PeriodsResponse } from '@/app/models/period';
import { useClosePeriodMutation, useOpenPeriodMutation } from '@/app/services/periodApi';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import StandardDialog from '@/components/common/StandardDialog';
import { DrawerType } from '@/pages/imports';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, TableCell, useTheme } from '@mui/material';
import { green, grey } from '@mui/material/colors';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OpenOrClosed from '../common/OpenOrClosed';
import StatusBadge from '../common/StatusBadge';
import SyncStatusBadge from './SyncStatusBadge';

interface PeriodTableProps {
  rows: PeriodsResponse[];
  setOpenDrawer: (drawer: DrawerType) => void;
  setSelectedPeriod: (drawer: PeriodsResponse) => void;
  selectedPeriod: PeriodsResponse;
  handleSyncSharing: CallableFunction;
  handleSyncEmployee: CallableFunction;
  handleProcess: CallableFunction;
  isDisabled: boolean;
}

export default function PeriodTable({
  rows,
  setOpenDrawer,
  setSelectedPeriod,
  selectedPeriod,
  handleSyncSharing,
  handleSyncEmployee,
  handleProcess,
  isDisabled,
}: PeriodTableProps) {
  const theme = useTheme();
  const { t } = useTranslation(['periods', 'common']);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const [closePeriod] = useClosePeriodMutation();
  const [openPeriod] = useOpenPeriodMutation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, status: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(status);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStatus(null);
  };

  const handleDrowers = (type: DrawerType) => {
    setOpenDrawer(type);
    setAnchorEl(null);
    setSelectedStatus(null);
  };

  const handleClosePeriod = (date: string) => {
    closePeriod(date);
    handleMenuClose();
  };

  const handleOpenPeriod = (date: string) => {
    openPeriod(date);
    handleMenuClose();
  };

  const selectedPeriodStatus = rows.find((row) => row.status === selectedStatus);

  const columns: Column[] = [
    { key: 'period', label: t('common:period'), align: 'center' },
    { key: 'status', label: t('common:status'), align: 'center' },
    { key: 'processing', label: t('common:processing'), align: 'center', width: theme.spacing(25) },
    { key: 'annex.8', label: t('common:annex.8'), align: 'center' },
    { key: 'ledger', label: t('common:ledger'), align: 'center' },
    { key: 'transport', label: t('common:transport'), align: 'center' },
    { key: 'collaborator', label: t('common:collaborator'), align: 'center' },
    { key: 'sharing', label: t('common:sharing'), align: 'center' },
    { key: 'actions', label: t('common:actions'), width: theme.spacing(30), align: 'right' },
  ];

  const openCloseStatusMap = {
    [Status.OPEN]: { label: 'opened', bgColor: green[200], color: grey[900] },
    [Status.CLOSED]: { label: 'closed', bgColor: grey[700] },
  };

  const renderRow = (row: PeriodsResponse) => (
    <>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{row.monthYear}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
        <OpenOrClosed status={row.status} statusKind={openCloseStatusMap} />
      </TableCell>
      <TableCell sx={{ ...bodyCellSx }}>
        <SyncStatusBadge status={row.processedStatus} importedData={row.processedDate} />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <StatusBadge
          isActive={row.financialReport.imported as boolean}
          importedData={row.financialReport.lastImportDate as string}
        />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <StatusBadge
          isActive={row.financialData.imported as boolean}
          importedData={row.financialData.lastImportDate as string}
        />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <StatusBadge
          isActive={row.transportRecife.imported as boolean}
          importedData={row.transportRecife.lastImportDate as string}
        />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <StatusBadge
          isActive={row.employees.imported as boolean}
          importedData={row.employees.lastImportDate as string}
        />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <StatusBadge isActive={row.shared.imported as boolean} importedData={row.shared.lastImportDate as string} />
      </TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <IconButton
          size="small"
          onClick={(e) => {
            handleMenuOpen(e, row.status);
            setSelectedPeriod(row);
          }}
          aria-label="more actions"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </>
  );

  const getRowKey = (row: PeriodsResponse) => row.id;

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.results.found')}
        stickyHeader
        size="small"
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedPeriodStatus?.status === 'CLOSED' && (
          <MenuItem
            onClick={() => {
              handleOpenPeriod(selectedPeriod.date);
            }}
          >
            {t('reopen')}
          </MenuItem>
        )}
        {selectedPeriodStatus?.status === 'OPENED' && (
          <>
            <MenuItem onClick={() => handleDrowers(DrawerType.SEND_ATTACHMENT)}>
              {t('update.attachment.eight')}
            </MenuItem>
            <MenuItem onClick={() => handleDrowers(DrawerType.SEND_REASON)}>{t('import.reason')}</MenuItem>
            <MenuItem onClick={() => handleDrowers(DrawerType.SEND_TRANSP_SPREADSHEET)}>
              {t('import.transport.spreadsheet')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleSyncSharing(selectedPeriod.date);
                handleMenuClose();
              }}
            >
              {t('synchronize.sharing')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleSyncEmployee(selectedPeriod.date);
                handleMenuClose();
              }}
            >
              {t('synchronize.collaborators')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClosePeriod(selectedPeriod.date);
              }}
            >
              {t('close.period')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setIsDeleteDialogOpen(true);
                setSelectedDate(selectedPeriod.date);
                handleMenuClose();
              }}
            >
              {t('common:process')}
            </MenuItem>
          </>
        )}
      </Menu>

      <StandardDialog
        title={t('confirmation.process.preloaded.data')}
        contentText={t('confirmation.process.warning')}
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          handleMenuClose();
        }}
        onConfirm={() => {
          if (selectedDate) {
            setIsDeleteDialogOpen(true);
            handleProcess(selectedDate);
          }
          setIsDeleteDialogOpen(false);
        }}
        cancelText={t('common:no')}
        confirmText={t('common:yes')}
        disableConfirm={isDisabled}
      />
    </>
  );
}
